<script lang="ts">
  import { onMount } from "svelte"
  import { Pulse } from "svelte-loading-spinners"
  import { xhr, toast } from "$app/utils"
  import Form from "./PlanMembers/Form.svelte"
  import MemberRow from "./PlanMembers/MemberRow.svelte"
  import AddNew from "./PlanMembers/AddNew.svelte"
  import Modal from "$app/components/Modal.svelte"
  import SubscriptionUsage from "./PlanMembers/SubscriptionUsage.svelte"
  import ModifyStandard from "./Billing/ModifyStandard.svelte"
  import UpgradeFree from "./Billing/UpgradeFree.svelte"
  import { ChevronDown, ChevronUp, Search } from "lucide-svelte"
  import { appStore } from "$app/store.svelte"
  import { signupStore } from "$app/signupstore.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import { slide } from "svelte/transition"

  let stripe_data = $state({} as StripeDataType)
  let stripe_upcoming_invoice = $state({})

  let showUpgradeFree = $state(false)
  let showModifyStandard = $state(false)

  let openModal = $state(false)

  let available_seats = $derived(signupStore.totalSeats - appStore.users.length)

  let isLoading = $state(true)
  async function initialize(): Promise<void> {
    isLoading = true
    try {
      const resp = await xhr.get<{
        users: UserType[]
        roles_hash: any
        alignment_only_members: any
        stripe_data: StripeDataType
        stripe_upcoming_invoice: any
      }>(`/plans/${appStore.plan.id}/admin/billing/member_data`)

      let data = resp.data

      appStore.users = Object.values(data.users)
      appStore.user_roles = data.roles_hash
      appStore.alignment_only_members = data.alignment_only_members

      stripe_data = data.stripe_data
      signupStore.stripe_data = data.stripe_data
      stripe_upcoming_invoice = data.stripe_upcoming_invoice
    } catch (e) {
      console.log(e)
      toast("There was an error loading the member data", "error")
    } finally {
      isLoading = false
    }
  }

  function addNewMember(user: UserTypeSubs) {
    openModal = false
    appStore.users = [...appStore.users, { ...user, edit: false }] as UserType[]
    filteredUsers = [...appStore.users]
  }

  let filteredUsers = $state([...appStore.users])
  let txtFilter = $state("")
  let sortVar = $state({ ord: "", field: "" })

  /**
   * Filter by name email
   */
  function filterByNameEmail(arr: any[]): any[] {
    if (txtFilter.trim() !== "") {
      const reg = new RegExp(txtFilter, "ig")
      return arr.filter((u) => {
        return reg.test(u.email) || reg.test(u.name)
      })
    } else {
      return arr
    }
  }

  /**
   * Filters all users
   */
  function filterUsers(): void {
    setTimeout(() => {
      filteredUsers = filterByNameEmail([...appStore.users])
      filteredUsers = sortByField(filteredUsers, sortVar.field, sortVar.ord)
    }, 20)
  }

  function sortByField(arr: any, field: string, dir: string): any[] {
    const { a, b } = dir === "desc" ? { a: -1, b: 1 } : { a: 1, b: -1 }

    return arr.sort((va, vb) => (va[field] >= vb[field] ? a : b))
  }

  function sortBy(field: string): void {
    if (sortVar.field !== field) {
      sortVar.ord = "asc"
    } else {
      sortVar.ord = sortVar.ord === "asc" ? "desc" : "asc"
    }
    sortVar.field = field

    filteredUsers = sortByField(filteredUsers, field, sortVar.ord)
  }

  sortBy("name")

  let showModal = $state(false)
  let migrate_member = $state(null)
  let migrate_role = $state("member")
  let saving = $state(false)

  async function migrateMember(user_id): Promise<void> {
    try {
      saving = true
      let resp = await xhr.patch(
        `/plans/${appStore.plan.id}/admin/members/migrate_alignment_only`,
        { user_id: user_id, role: migrate_role }
      )

      //updateUsers(resp.data.users)
      appStore.alignment_only_members = resp.data.alignment_only_members

      toast("Alignment-Only Member Migrated", "success")
    } catch (e: any) {
      Honeybadger.notify(e)
      toast("There was an error migrating the alignment-only member", "error")
    } finally {
      saving = false
      showModal = false
    }
  }

  let selectedUser: UserType | null = $state(null)

  onMount(async () => {
    initialize()
  })
</script>

<svelte:head>
  <script src="https://js.stripe.com/v3/"></script>
</svelte:head>

<div class="flex flex-col">
  <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
    <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
      <div class="flex justify-between items-start">
        <h2 class="text-2xl lg:text-3xl font-semibold pb-1 mb-3">Members</h2>
        <div>
          {#if !showUpgradeFree && !showModifyStandard && signupStore.subscription.subscription_type !== "unlimited"}
            <button
              onclick={() => {
                signupStore.subscription.subscription_type === "free"
                  ? (showUpgradeFree = true)
                  : (showModifyStandard = true)
              }}
              class=" !text-blue-500 px-4 border border-blue-100 py-2 rounded hover:bg-blue-50"
            >
              {#if signupStore.subscription.subscription_type === "free"}
                Upgrade to Standard
              {:else}
                Modify Subscription
              {/if}
            </button>
          {:else if showUpgradeFree && !isLoading}
            <button
              onclick={() => {
                showUpgradeFree = false
              }}
              class=" !text-blue-500 px-4 border border-blue-100 py-2 rounded hover:bg-blue-50"
            >
              Cancel Upgrade
            </button>
          {/if}
        </div>
      </div>

      {#if isLoading}
        <div class="py-12 flex justify-center w-full">
          <Pulse size="240" color="#cdcdcd" unit="px" duration="1s" />
        </div>
      {:else if showUpgradeFree}
        <div class="mt-4">
          <UpgradeFree oncomplete={() => (showUpgradeFree = false)} />
        </div>
      {:else if showModifyStandard}
        <div class="mt-4">
          <ModifyStandard
            oncomplete={() => (showModifyStandard = false)}
            oncancel={() => (showModifyStandard = false)}
          />
        </div>
      {:else}
        <div class="py-2">
          <p class="text-lg">
            Easily view and manage your members. To add or remove members, or
            adjust roles of current members click the <span class="font-bold">
              Edit Subscription
            </span> button.
          </p>
        </div>
        <div class="mt-4 py-4 px-6 border border-gray-200 rounded">
          {#if isLoading}
            <div class="py-12 flex justify-center w-full">
              <Pulse size="240" color="#cdcdcd" unit="px" duration="1s" />
            </div>
          {:else}
            <div class="flex justify-between items-center">
              <div>
                {#if signupStore.availableSeats > 0 || signupStore.subscription.subscription_type === "unlimited"}
                  <button
                    class="btn !text-blue-500 outline outline-blue-500"
                    onclick={() => (openModal = true)}
                  >
                    Add New Member
                  </button>
                {/if}
              </div>
              <div class="relative">
                <div
                  class="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center w-full"
                >
                  <Search class="h-5 w-5 text-gray-400" />
                </div>
                <input
                  class="block w-full sm:w-72 bg-white border border-gray-300 rounded-md py-2 pl-10 pr-3 text-sm placeholder-gray-500 focus:outline-none focus:text-gray-900 focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Type name or email to filter"
                  bind:value={txtFilter}
                  onkeyup={filterUsers}
                  type="search"
                />
              </div>
            </div>
            <div
              class="mt-4 grid grid-cols-12 w-full py-2 border-b border-gray-200"
            >
              <button
                class="col-span-5 cursor-pointer flex items-center"
                onclick={() => sortBy("name")}
                tabindex="0"
              >
                {#if sortVar.field === "name" && sortVar.ord === "asc"}
                  <ChevronUp class="text-gray-400 w-4 h-4" />
                {:else}
                  <ChevronDown class="text-gray-400 w-4 h-4" />
                {/if}
                Name
              </button>
              <div class="col-span-2">Role</div>
              <button
                class="col-span-2 cursor-pointer flex items-center"
                onclick={() => sortBy("role")}
                tabindex="0"
              >
                {#if sortVar.field === "role" && sortVar.ord === "asc"}
                  <ChevronUp class="text-gray-400 w-4 h-4" />
                {:else}
                  <ChevronDown class="text-gray-400 w-4 h-4" />
                {/if}
                Permission
              </button>
              <button
                class="col-span-2 cursor-pointer flex items-center"
                onclick={() => sortBy("last_sign_in_at")}
                tabindex="0"
              >
                {#if sortVar.field === "last_sign_in_at" && sortVar.ord === "asc"}
                  <ChevronUp class="text-gray-400 w-4 h-4" />
                {:else}
                  <ChevronDown class="text-gray-400 w-4 h-4" />
                {/if}
                Last Access
              </button>
              <div class="col-span-1"></div>
            </div>

            {#each filteredUsers as user (user.id)}
              <div class="border-b border-gray-200">
                {#if selectedUser?.id === user.id}
                  <div transition:slide>
                    <Form
                      {user}
                      oncancel={() => {
                        selectedUser = null
                      }}
                      onupdate={(data: UserType) => {
                        let idx = appStore.users.findIndex(
                          (u) => u.id === data.id
                        )
                        appStore.users[idx] = {
                          ...appStore.users[idx],
                          ...data
                        }
                        selectedUser = null
                        filterUsers()
                      }}
                    />
                  </div>
                {:else}
                  <MemberRow
                    {user}
                    onedit={(user) => {
                      selectedUser = user
                    }}
                    ondelete={(user) => {
                      appStore.users = appStore.users.filter(
                        (u) => u.id !== user.id
                      )
                      filteredUsers = [...appStore.users]
                    }}
                    onresend={(user) => {
                      toast("The invitation has been resent.", "success")
                    }}
                  />
                {/if}
              </div>
            {/each}
          {/if}
        </div>
      {/if}
    </div>
  </div>
</div>

{#if !isLoading && !showUpgradeFree && !showModifyStandard}
  <div class="py-6">
    <SubscriptionUsage />
  </div>
{/if}

<Modal closeOnOuterClick={true} bind:open={openModal}>
  <AddNew oncreate={addNewMember} />
</Modal>

{#if appStore.alignment_only_members.length > 0}
  <div class="px-6 py-4 mx-8 my-4 border-t border-gray-200">
    <h3 class="text-xl font-bold text-left">Alignment-Only Members</h3>

    <p class="mt-4 text-amber-600 bg-amber-50 rounded shadow py-2 px-4">
      You have the following <strong>Alignment-Only Members</strong> from your
      Alignment Test. You can migrate any of these members to become plan
      members if you have available seats for your subscription.
      {#if available_seats > 0}
        You currently have <strong> {available_seats} available seats</strong>.
      {:else}
        You have no available seats at this time, please update your
        subscription to add more seats.
      {/if}
    </p>
    <table class="min-w-full divide-y divide-gray-300 mt-8">
      <thead class="bg-gray-50">
        <tr>
          <th
            scope="col"
            class="py-1 px-3 pr-3 text-left text-sm font-semibold text-gray-600"
            >Name</th
          >
          <th
            scope="col"
            class="px-3 py-1 text-left text-sm font-semibold text-gray-600"
            >Email</th
          >
          <th
            scope="col"
            class="py-1 px-3 text-center text-sm font-semibold text-gray-600"
            >Action</th
          >
        </tr>
      </thead>

      <tbody class="divide-y divide-gray-200 bg-white">
        {#each appStore.alignment_only_members as user (user.id)}
          <tr>
            <td
              class="whitespace-nowrap py-3 px-3 text-sm font-medium text-gray-600"
              >{user.name}</td
            >
            <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-600"
              >{user.email}</td
            >
            <td
              class="whitespace-nowrap py-3 px-3 text-sm font-medium text-center"
            >
              <button
                onclick={() => {
                  migrate_member = user
                  showModal = true
                }}
                tabindex="0"
                class="text-indigo-600 hover:text-indigo-700 border py-1 px-3 bg-white border-indigo-100 hover:bg-indigo-50 rounded"
                >Convert to Plan Member</button
              >
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<Modal bind:open={showModal}>
  <h3 class="text-center text-xl pr-6 font-bold">Confirm Member Migration</h3>
  {#if saving}
    <div class="py-12 flex justify-center w-full">
      <Pulse size="240" color="#cdcdcd" unit="px" duration="1s" />
    </div>
  {:else}
    <h4 class="text-left text-lg mt-4">
      Are you sure you want to migrate this alignment-only member
      <strong>{migrate_member.name}</strong> to be a Plan Member?
    </h4>
    <div class="mt-4 italic">
      This will use one of your unused member spots for your subscription.
    </div>

    <div class="flex justify-center py-4 px-4 mt-4 items-center">
      <p class="text-base w-1/2 text-right mr-4 font-bold">
        Select Role of Migrated Member
      </p>
      <select
        bind:value={migrate_role}
        class=" block rounded-md border-0 w-1/2 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm/6"
      >
        {#each Object.entries(appStore.user_roles) as [id, name]}
          <option value={id}>{name}</option>
        {/each}
      </select>
    </div>

    <div class="text-right mt-6">
      <button
        class="btn bg-gray-200 hover:bg-gray-300 mr-8"
        onclick={() => (showModal = false)}
        tabindex="0"
      >
        Cancel
      </button>

      <button
        class="btn bg-blue-600 hover:bg-blue-800 text-white hover:shadow"
        onclick={() => migrateMember(migrate_member.id)}
        tabindex="0"
      >
        Convert to Plan Member
      </button>
    </div>
  {/if}
</Modal>
